import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

type CreditPolicy = {
  enabled: boolean;
  tiers: { name: string; amount: number; bonusPercent: number; totalCredit: number }[];
  allowPartialCredit: boolean;
  minimumTopUp: number;
  creditExpiryMonths: number;
};

type TransactionDto = {
  id: string;
  type: string;
  amount: number;
  reason: string;
  description: string;
  balanceAfter: number;
  createdAt: string;
};

type TransactionsResponse = {
  transactions: TransactionDto[];
  pagination: { page: number; size: number; totalElements: number; totalPages: number };
};

type TopUpRequest = {
  tenantId: string;
  amount: number;
  bonusPercent: number;
  paymentMethodId: string;
  tierName: string;
};

type TopUpResponse = {
  balance: number;
  credited: number;
  bonus: number;
  stripePaymentIntentId: string;
};

type WalletDto = {
  userId: string;
  customerName: string;
  customerEmail: string;
  balance: number;
};

export function useCreditPolicy(tenantId?: string) {
  return useQuery({
    queryKey: ['creditPolicy', tenantId],
    queryFn: () => apiClient<CreditPolicy>(`/users/me/credits/policy?tenantId=${tenantId}`),
    enabled: !!tenantId,
  });
}

export function useCreditTransactions(page = 0, size = 20) {
  return useQuery({
    queryKey: ['creditTransactions', page, size],
    queryFn: () => apiClient<TransactionsResponse>(`/users/me/credits/transactions?page=${page}&size=${size}`),
  });
}

export function useTopUpCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: TopUpRequest) =>
      apiClient<TopUpResponse>('/users/me/credits/topup', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creditBalance'] });
      qc.invalidateQueries({ queryKey: ['creditTransactions'] });
    },
  });
}

export function useAdminCreditWallets() {
  return useQuery({
    queryKey: ['adminCreditWallets'],
    queryFn: () => apiClient<WalletDto[]>('/admin/credits/wallets'),
  });
}

export function useAdminCreditAdjust() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: { userId: string; amount: number; reason: string }) =>
      apiClient<{ balance: number }>('/admin/credits/adjust', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminCreditWallets'] });
      qc.invalidateQueries({ queryKey: ['adminCustomers'] });
    },
  });
}
